import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PrimitiveAtom, useAtom } from "jotai";

import React from "react";
import { useMenu } from "src/components/Menu";
import { KeyCode } from "src/utils/keys";
import styled from "styled-components";

import {
  planogramSelectedItemsAtom,
  planogramSelectedShelfAtom,
  planogramSelectedShelvesAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { Color, Flex, Text } from "src/elements";
import { Scaleable } from "../../store/types";
import { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import { usePogAtomActions } from "../../store/actions";
import { getNewShelfNotchNo } from "../../store/utils";
import { usePlanogramPanel } from "../Panel/store/hooks";
import { DEFAULT_NOTCH_RULER_WIDTH } from "../Ruler/PdfNotchRulers";
import { layoutAisleBreakCaption, SPLIT_GAP } from "../KonvaPlanogram/sceneProjection";
import { useTranslation } from "src/i18n";

const BayElement = styled(Flex)<{
  xLeft: number;
  bayWidth: number;
  bayHeight: number;
  maxNotch: number;
  notchSpacing: number;
  notchOffset: number;
  scaleX: number;
  scaleY: number;
  pdfMode?: boolean;
  pdfNotchRulerWidth?: number;
  splitGapAfter: boolean;
}>`
  position: relative;
  flex-direction: column;
  /* Keep shorter bays anchored to the planogram floor so the bay label does not float. */
  align-self: flex-end;
  min-width: ${({ bayWidth, scaleX }) => `calc(${bayWidth}px * ${scaleX})`};
  height: ${({ bayHeight, notchOffset, notchSpacing, maxNotch, scaleY }) =>
    `calc(${Math.max(bayHeight, notchOffset + maxNotch * notchSpacing)}px * ${scaleY})`};
  background-color: ${Color.sand50};
  pointer-events: all;

  ${({ pdfMode, pdfNotchRulerWidth = DEFAULT_NOTCH_RULER_WIDTH, scaleX }) =>
    pdfMode && `margin-left: calc(${pdfNotchRulerWidth}px * ${scaleX});`}
  margin-right: ${({
    pdfMode,
    pdfNotchRulerWidth = DEFAULT_NOTCH_RULER_WIDTH,
    splitGapAfter,
    scaleX,
  }) =>
    `calc(${(pdfMode ? pdfNotchRulerWidth : 0) + (splitGapAfter ? SPLIT_GAP : 0)}px * ${scaleX})`};

  &:not(:first-of-type) {
    border-left: 1px dashed rgba(0, 0, 0, 0.7);
  }

  &:not(:last-of-type) {
    border-right: 1px dashed rgba(0, 0, 0, 0.7);
  }
`;

/** The gap the run breaks at, drawn in the bay's right margin, past its PDF ruler. */
const AisleBreakGap = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(100% + ${({ $left }) => $left}px);
  width: ${({ $width }) => $width}px;
  pointer-events: none;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: calc(50% - 1px);
    border-left: 2px dashed ${Color.spaceGray};
  }
`;

const AisleBreakCaption = styled.span<{ $left: number; $fontSize: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(50% + ${({ $left }) => $left}px);
  writing-mode: vertical-rl;
  text-align: center;
  font-size: ${({ $fontSize }) => $fontSize}px;
  font-weight: bold;
  line-height: 1;
  color: ${Color.spaceGray};
  white-space: nowrap;
`;

const BaseHeight = styled(Flex)<{
  baseHeight: number;
  scaleY: number;
}>`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: ${({ baseHeight, scaleY }) => `calc(${baseHeight}px * ${scaleY})`};
  background: linear-gradient(180deg, rgba(177, 163, 145, 1) 0%, rgba(154, 139, 120, 1) 100%);
  border-radius: 2px;
  justify-content: center;
  align-items: center;
  text-shadow: 1px 1px 0px rgba(255, 255, 255, 0.3);
  opacity: 0.7;
  overflow: hidden;
`;

const NotchLines = styled(Flex)<{
  bayHeight: number;
  notchOffset: number;
  scaleY: number;
}>`
  position: absolute;
  flex-grow: 1;
  bottom: ${({ notchOffset, scaleY }) => `calc(${notchOffset}px * ${scaleY})`};
  left: 0;
  width: 100%;
  height: ${({ bayHeight, scaleY }) => `calc(${bayHeight}px * ${scaleY})`};
`;

const NotchLine = styled(Flex)<{
  notchSpacing: number;
  notchNo: number;
  scaleY: number;
}>`
  width: 100%;
  height: 2px;
  background-color: ${Color.textDisabled};
  position: absolute;
  left: 0;
  bottom: ${({ notchNo, notchSpacing, scaleY }) =>
    `calc((${notchNo - 1} * ${notchSpacing}px) * ${scaleY})`};
  opacity: 0.25;
  pointer-events: none;
`;

interface Props extends Scaleable {
  bay: PlanogramBay;
  isEditable: boolean;
  pogAtom: PrimitiveAtom<Pog>;
  children?: React.ReactNode;
  pdfMode?: boolean;
  pdfNotchRulerWidth?: number;
  /** The run breaks after this bay, so a gap follows it. */
  splitGapAfter?: boolean;
}

export const Bay = ({
  bay,
  bay: { uniqueId, baseHeight, maxNotch, notchSpacing },
  scaleX,
  scaleY,
  isEditable,
  pogAtom,
  children,
  pdfMode = false,
  pdfNotchRulerWidth,
  splitGapAfter = false,
}: Props) => {
  const { t } = useTranslation("common");
  const { addNewShelf, deleteBay, addBayToSide } = usePogAtomActions(pogAtom);
  const { openMenu } = useMenu();
  const { tab, setTab } = usePlanogramPanel();

  const [selectedShelf, setSelectedShelf] = useAtom(planogramSelectedShelfAtom);
  const [selectedShelfIds, setSelectedShelfIds] = useAtom(planogramSelectedShelvesAtom);
  const [selectedItems, setSelectedItems] = useAtom(planogramSelectedItemsAtom);

  const startAddNewShelf = (event: React.MouseEvent<HTMLDivElement>) => {
    const newShelf = addNewShelf(bay.getNo(), getNewShelfYBottomFromCursor(event));
    setSelectedShelf(newShelf);
    setSelectedShelfIds(newShelf ? [newShelf.uniqueId] : []);
    setTab("ADD_EDIT_FIXTURE");
  };

  const getNewShelfYBottomFromCursor = (event: React.MouseEvent<HTMLDivElement>) => {
    const cursorYTop = event.clientY;
    const { top, height } = (event.target as HTMLDivElement).getBoundingClientRect();

    return getNewShelfNotchNo({
      bayHeight: height,
      cursorY: cursorYTop - top,
      maxNotch: bay.maxNotch,
      notchSpacing: bay.notchSpacing,
      scaleY,
    });
  };

  const deleteSelectedBay = () => {
    // A refused delete leaves the bay, so its shelves stay selected.
    if (!deleteBay(bay)) return;
    const deletedShelfIds = new Set(bay.shelves.map((shelf) => shelf.uniqueId));
    setSelectedShelfIds((current) => current.filter((shelfId) => !deletedShelfIds.has(shelfId)));
    if (selectedShelf && deletedShelfIds.has(selectedShelf.uniqueId)) {
      setSelectedShelf(null);
    }
  };

  const openBayMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isEditable) return;

    const options = [
      {
        label: "New Shelf",
        keybind: {
          keyCode: KeyCode.N,
        },
        onClick: () => startAddNewShelf(event),
      },
      {
        label: "Add Bay Left",
        keybind: {
          keyCode: KeyCode.Q,
        },
        onClick: () => addBayToSide(bay, "LEFT"),
      },
      {
        label: "Add Bay Right",
        keybind: {
          keyCode: KeyCode.E,
        },
        onClick: () => addBayToSide(bay, "RIGHT"),
      },
      {
        label: "Delete Bay",
        keybind: {
          keyCode: KeyCode.DELETE,
        },
        onClick: deleteSelectedBay,
      },
    ];

    openMenu({
      event,
      menu: {
        title: `Bay ${bay.getNo()}`,
        options,
      },
    });
  };

  const gapWidth = SPLIT_GAP * scaleX;
  const caption = layoutAisleBreakCaption(gapWidth);

  const unselectShelfAndItems = () => {
    if (selectedShelf !== null || selectedShelfIds.length > 0) {
      setSelectedShelf(null);
      setSelectedShelfIds([]);
      setTab("FIXTURES_DETAILS");
    }

    if (selectedItems.length > 0) {
      setSelectedItems([]);
      const overrideTabsLock = tab === "PRODUCT_DETAILS";
      setTab("HIGHLIGHTS", overrideTabsLock);
    }
  };

  return (
    <BayElement
      data-id={uniqueId}
      xLeft={bay.getXLeft()}
      bayWidth={bay.width}
      bayHeight={bay.height}
      notchOffset={bay.notchOffset}
      notchSpacing={bay.notchSpacing}
      maxNotch={bay.maxNotch}
      scaleX={scaleX}
      scaleY={scaleY}
      pdfMode={pdfMode}
      pdfNotchRulerWidth={pdfNotchRulerWidth}
      splitGapAfter={splitGapAfter}
    >
      <NotchLines
        bayHeight={bay.height}
        notchOffset={bay.notchOffset}
        scaleY={scaleY}
        onContextMenu={openBayMenu}
        onClick={unselectShelfAndItems}
        cursor="default"
      >
        {Array.from(Array(maxNotch).keys()).map((notchIndex) => (
          <NotchLine
            key={notchIndex}
            notchSpacing={notchSpacing}
            notchNo={notchIndex + 1}
            scaleY={scaleY}
          />
        ))}
      </NotchLines>

      <BaseHeight baseHeight={baseHeight} scaleY={scaleY}>
        <Text
          variant="caption1"
          color={Color.sand700}
          textTransform="uppercase"
          whiteSpace="nowrap"
        >
          Bay {bay.getNo()}
        </Text>
      </BaseHeight>

      <React.Fragment>{children}</React.Fragment>

      {splitGapAfter && (
        <AisleBreakGap
          data-testid="aisle-break-gap"
          $left={(pdfMode ? (pdfNotchRulerWidth ?? DEFAULT_NOTCH_RULER_WIDTH) : 0) * scaleX}
          $width={gapWidth}
        >
          {caption && (
            <AisleBreakCaption
              $left={caption.centerOffset - caption.fontSize / 2}
              $fontSize={caption.fontSize}
            >
              {t("cdtPriorityOrder.aisleBreak")}
            </AisleBreakCaption>
          )}
        </AisleBreakGap>
      )}
    </BayElement>
  );
};
